/**
 * Build identifier pill, fixed bottom-right above the Dock.
 *
 * Purpose: visually confirm whether a deploy has rolled through. The
 * SHA + build-time UTC stamp are injected at build time by Vite's
 * `define` config — see apps/mini-app/vite.config.ts.
 *
 * The badge starts at 50% opacity so it doesn't compete with content;
 * hovering / focusing brings it to full opacity. Tap copies the SHA
 * to the clipboard for paste-into-git scenarios.
 */

import { useState } from 'react';

// Build-time-injected constants. See vite.config.ts `define` block.
declare const __BUILD_SHA__: string;
declare const __BUILD_TIME__: string;

function formatStamp(iso: string): string {
  // iso example: '2026-05-22T07:34:18.000Z' → '05-22 07:34Z'
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mm   = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd   = String(d.getUTCDate()).padStart(2, '0');
  const hh   = String(d.getUTCHours()).padStart(2, '0');
  const mins = String(d.getUTCMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${mins}Z`;
}

export function BuildBadge() {
  const sha   = __BUILD_SHA__;
  const stamp = formatStamp(__BUILD_TIME__);
  const [copied, setCopied] = useState(false);

  async function onClick() {
    try {
      await navigator.clipboard?.writeText(sha);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked in some Telegram WebViews — no-op */
    }
  }

  return (
    <button
      type="button"
      className="build-badge"
      onClick={onClick}
      title={`Build ${sha} · ${stamp} · tap to copy SHA`}
      aria-label={`Build ${sha}, built ${stamp} UTC`}
    >
      <span className="build-badge__sha">{copied ? 'copied' : sha}</span>
      <span className="build-badge__time">{stamp}</span>
    </button>
  );
}
